import { Controller } from "@hotwired/stimulus"
import RequestHelper from "../helpers/request_helper"

// Mounted on `.platform-license`, the nearest ancestor of the toggle inputs, the
// gift-sends/contract-term fields and their "Edit" links. The activity table container and its
// filter select are page-level siblings rather than descendants, so they stay a
// `document.getElementById` lookup with the id passed in as a value - wrapping them to make them
// targets would restructure the layout.
//
// The activity table is refreshed through the `datatable` controller's public `reload()`, reached
// with getControllerForElementAndIdentifier.
export default class extends Controller {
  static get targets() {
    return ["giftSends", "contractTerm", "editGiftSends", "editContractTerm"];
  }

  static get values() {
    return {
      url: String,
      enabled: Boolean,
      expired: Boolean,
      activityContainerId: String,
      filterSelectId: String,
    };
  }

  connect() {
    this.updateEditLinks();
  }

  toggleOn() {
    this.toggle(true, "activation");
  }

  toggleOff() {
    this.toggle(false, "deactivation");
  }

  toggle(isEnabled, updateType) {
    if (isEnabled === this.enabledValue) {
      return;
    }

    const options = RequestHelper.defaultPatchOptions();
    options.headers = Object.assign({}, options.headers, { "Content-Type": "application/json" });
    options.body = JSON.stringify({
      organization_platform_license: { enabled: isEnabled },
      update_type: updateType,
    });

    fetch(this.urlValue, options).
      then((response) => (response.ok ? response.json() : null)).
      then((data) => {
        if (!data) { return; }

        this.enabledValue = data.enabled;
        this.expiredValue = data.status === "expired";

        this.giftSendsTarget.value = data.gift_sends;
        this.contractTermTarget.value = data.contract_term;

        window.ltAlert({ message: "Changes saved" });

        this.toggleActivityTable(this.enabledValue);
        this.updateEditLinks();
        this.reloadActivityTable();
      });
  }

  toggleActivityTable(enabled) {
    const container = document.getElementById(this.activityContainerIdValue);
    const filterSelect = document.getElementById(this.filterSelectIdValue);

    if (container) { container.classList.toggle("inactive", !enabled); }
    if (filterSelect) { filterSelect.disabled = !enabled; }
  }

  // The container is a page-level sibling of this mount (see the header), so it is found by id
  // and then handed to Stimulus's controller lookup - the target is another controller instance,
  // not a DOM node this one owns.
  reloadActivityTable() {
    const container = document.getElementById(this.activityContainerIdValue);
    if (!container) { return; }

    const datatable = this.application.getControllerForElementAndIdentifier(container, "datatable");
    if (datatable) { datatable.reload(); }
  }

  updateEditLinks() {
    this.setDisabled(this.editGiftSendsTarget, !this.enabledValue);
    this.setDisabled(this.editContractTermTarget, !this.enabledValue || this.expiredValue);
  }

  setDisabled(element, disabled) {
    if (disabled) {
      element.setAttribute("disabled", "disabled");
    } else {
      element.removeAttribute("disabled");
    }
  }
}
